import React, { useState, useEffect } from 'react';
import { Shield, Clock, Save, Loader2, AlertTriangle, CheckCircle } from 'lucide-react';
import { useAuth } from '../../context/AuthContext';

const API_BASE = process.env.REACT_APP_BACKEND_URL;

const CommercialApprovalSettings = () => {
  const { token } = useAuth();
  const [timeoutHours, setTimeoutHours] = useState(48);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState({ type: '', text: '' });

  useEffect(() => {
    const fetchSettings = async () => {
      try {
        const res = await fetch(`${API_BASE}/api/settings/commercial-approval-timeout`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (res.ok) {
          const data = await res.json();
          setTimeoutHours(data.timeout_hours || 48);
        }
      } catch {
        /* ignore */
      }
      setLoading(false);
    };
    fetchSettings();
  }, [token]);

  useEffect(() => {
    if (message.text) {
      const t = setTimeout(() => setMessage({ type: '', text: '' }), 4000);
      return () => clearTimeout(t);
    }
  }, [message]);

  const handleSave = async () => {
    if (timeoutHours < 1) {
      setMessage({ type: 'error', text: 'Timeout must be at least 1 hour' });
      return;
    }
    setSaving(true);
    try {
      const res = await fetch(`${API_BASE}/api/settings/commercial-approval-timeout`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ timeout_hours: parseInt(timeoutHours) }),
      });
      if (!res.ok) {
        const err = await res.json();
        throw new Error(err.detail || 'Failed to save');
      }
      const data = await res.json();
      setTimeoutHours(data.timeout_hours);
      setMessage({ type: 'success', text: 'Settings saved successfully' });
    } catch (e) {
      setMessage({ type: 'error', text: e.message });
    }
    setSaving(false);
  };

  if (loading) {
    return (
      <div className="flex justify-center py-12">
        <Loader2 size={24} className="animate-spin text-teal-600" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="commercial-approval-settings">
      <div>
        <h2 className="text-xl font-bold text-gray-800 flex items-center gap-2">
          <Shield size={22} className="text-teal-600" />
          Commercial Approval Settings
        </h2>
        <p className="text-sm text-gray-500 mt-1">
          Configure the escalation timeout for the commercial approval workflow.
        </p>
      </div>

      {message.text && (
        <div
          className={`flex items-center gap-2 px-4 py-3 rounded-xl text-sm font-medium ${
            message.type === 'success'
              ? 'bg-emerald-50 text-emerald-700 border border-emerald-200'
              : 'bg-red-50 text-red-700 border border-red-200'
          }`}
          data-testid="settings-message"
        >
          {message.type === 'success' ? <CheckCircle size={16} /> : <AlertTriangle size={16} />}
          {message.text}
        </div>
      )}

      <div className="bg-gray-50 border border-gray-200 rounded-2xl p-6 space-y-5">
        <div>
          <h3 className="text-sm font-semibold text-gray-700 flex items-center gap-2 mb-1">
            <Clock size={16} className="text-gray-500" />
            CX Leader Escalation Timeout
          </h3>
          <p className="text-xs text-gray-500 mb-4">
            If the Executive does not approve within this period (after BFR approval),
            the CX Leader will be allowed to approve instead.
          </p>

          <div className="flex items-center gap-3">
            <input
              type="number"
              min={1}
              value={timeoutHours}
              onChange={(e) => setTimeoutHours(e.target.value)}
              className="w-28 px-3 py-2.5 border border-gray-300 rounded-xl text-sm focus:ring-2 focus:ring-teal-500 outline-none"
              data-testid="timeout-hours-input"
            />
            <span className="text-sm text-gray-600 font-medium">hours</span>
          </div>

          <p className="text-xs text-gray-400 mt-2">
            Default: 48 hours. Minimum: 1 hour.
          </p>
        </div>

        <div className="border-t border-gray-200 pt-4">
          <h4 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-3">
            Approval Flow Reference
          </h4>
          <div className="flex items-center gap-2 text-xs text-gray-600 flex-wrap">
            <span className="px-2 py-1 bg-amber-100 text-amber-700 rounded-lg font-medium">
              GTM Leader
            </span>
            <span className="text-gray-400">&rarr;</span>
            <span className="px-2 py-1 bg-blue-100 text-blue-700 rounded-lg font-medium">
              BFR Team
            </span>
            <span className="text-gray-400">&rarr;</span>
            <span className="px-2 py-1 bg-indigo-100 text-indigo-700 rounded-lg font-medium">
              Executive
            </span>
            <span className="text-gray-400">&rarr;</span>
            <span className="px-2 py-1 bg-emerald-100 text-emerald-700 rounded-lg font-medium">
              Verified
            </span>
          </div>
          <p className="text-xs text-gray-400 mt-2">
            If Executive times out, CX Leader can step in to finalize.
          </p>
        </div>
      </div>

      <div className="flex justify-end">
        <button
          onClick={handleSave}
          disabled={saving}
          className="px-5 py-2.5 bg-teal-600 text-white rounded-xl text-sm font-medium hover:bg-teal-700 disabled:opacity-50 flex items-center gap-2 transition-colors"
          data-testid="save-timeout-btn"
        >
          {saving ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />}
          Save Settings
        </button>
      </div>
    </div>
  );
};

export default CommercialApprovalSettings;
